import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DataTable, FilterBar, ConfirmDialog } from '../shared';
import { listUsers, createUser, updateUserStatus, updateUser, generatePassword, resendCredentials } from '../../services/userManagementService';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../theme/tokens';
import { getUserStatus, getStatusColor } from '../../utils/statusUtils';
import toast from 'react-hot-toast';
import EmailIcon from '@mui/icons-material/Email';
import Tooltip from '@mui/material/Tooltip';

/**
 * ClinicStaffManagement Component
 * 
 * Comprehensive clinic staff management interface for clinic admins.
 * Features: list, search, filter, view, edit, create, activate/deactivate staff.
 * Multi-tenant with strict tenantId enforcement.
 */
const ClinicStaffManagement = () => {
  const { profile } = useAuth();
  
  // Table state
  const [staff, setStaff] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  // Create form state
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
  });

  // Fetch staff
  const fetchStaff = useCallback(async () => {
    if (!profile?.tenant_id) {
      setError('No tenant context available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Convert 0-indexed page (for Material-UI) to 1-indexed (for backend API)
      const apiPage = page + 1;
      
      const result = await listUsers({
        tenantId: profile.tenant_id,
        role: 'clinic_staff',
        q: searchTerm,
        status: statusFilter,
        page: apiPage,
        pageSize,
        sortBy,
        sortOrder,
      });

      if (result.error) {
        throw result.error;
      }

      setStaff(result.data.rows);
      setTotalCount(result.data.total);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError('Failed to load clinic staff. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.tenant_id, searchTerm, statusFilter, page, pageSize, sortBy, sortOrder]);

  // Load staff on mount and when dependencies change
  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A',
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
    },
    {
      key: 'phone',
      header: 'Phone',
      width: 150,
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: 120,
      render: (row) => {
        const displayStatus = getUserStatus(row.status);
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
          />
        );
      },
    },
    {
      key: 'created_at',
      header: 'Added',
      sortable: true,
      width: 150,
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
    },
  ];

  // Action handlers
  const handleView = (staffMember) => {
    setSelectedStaff(staffMember);
    setViewDialogOpen(true);
  };

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    setEditForm({
      first_name: staffMember.first_name || '',
      last_name: staffMember.last_name || '',
      email: staffMember.email || '',
      phone: staffMember.phone || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeactivate = (staffMember) => {
    setSelectedStaff(staffMember);
    setConfirmAction('deactivate');
    setConfirmDialogOpen(true);
  };

  const handleActivate = (staffMember) => {
    setSelectedStaff(staffMember);
    setConfirmAction('activate');
    setConfirmDialogOpen(true);
  };

  const handleCreate = () => {
    setCreateForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: generatePassword(),
    });
    setCreateDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedStaff || !profile?.tenant_id) return;

    const newStatus = confirmAction === 'activate' ? 'active' : 'inactive';
    
    const result = await updateUserStatus(
      selectedStaff.id,
      profile.tenant_id,
      newStatus
    );

    if (result.error) {
      toast.error(`Failed to ${confirmAction} staff member`);
    } else {
      await fetchStaff();
    }

    setConfirmDialogOpen(false);
    setSelectedStaff(null);
    setConfirmAction(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedStaff || !profile?.tenant_id) return;

    setLoading(true);
    try {
      const result = await updateUser(
        selectedStaff.id,
        profile.tenant_id,
        editForm
      );

      if (result.error) {
        throw result.error;
      }

      await fetchStaff();
      setEditDialogOpen(false);
      setSelectedStaff(null);
    } catch (err) {
      console.error('Error updating staff:', err);
      toast.error('Failed to update staff member');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async () => {
    if (!profile?.tenant_id) return;

    setLoading(true);
    try {
      const result = await createUser({
        tenantId: profile.tenant_id,
        tenant_role: 'clinic_staff',
        status: 'active',
        ...createForm,
      });

      if (result.error) {
        throw result.error;
      }

      await fetchStaff();
      setCreateDialogOpen(false);
      toast.success('Staff member created! Make sure to save the password.');
    } catch (err) {
      console.error('Error creating staff:', err);
      toast.error('Failed to create staff member');
    } finally {
      setLoading(false);
    }
  };

  const regeneratePassword = () => {
    setCreateForm({ ...createForm, password: generatePassword() });
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(createForm.password);
    toast.success('Password copied to clipboard!');
  };

  // Handle resend credentials
  const handleResendCredentials = async (userId) => {
    setResendingEmail(userId);
    try {
      const result = await resendCredentials(userId);
      if (result.error) {
        throw result.error;
      }
      // Show password in toast if email might have failed
      if (result.data?.data?.password) {
        toast.success(
          `Credentials email sent! Password: ${result.data.data.password}\n\n⚠️ If email fails, share this password manually.`,
          { duration: 10000 }
        );
      } else {
        toast.success('Credentials email sent successfully!');
      }
    } catch (error) {
      console.error('Error resending credentials:', error);
      toast.error('Failed to resend credentials email');
    } finally {
      setResendingEmail(null);
    }
  };

  // Actions slot for table
  const actionsSlot = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <Tooltip title="Resend credentials email">
        <IconButton
          size="small"
          onClick={() => handleResendCredentials(row.id)}
          disabled={resendingEmail === row.id}
          color="primary"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          <EmailIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        onClick={() => handleView(row)}
        aria-label={`View ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleEdit(row)}
        aria-label={`Edit ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      {row.status === 'active' ? (
        <IconButton
          size="small"
          onClick={() => handleDeactivate(row)}
          aria-label={`Deactivate ${row.first_name} ${row.last_name}`}
          color="error"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.danger}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonOffIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={() => handleActivate(row)}
          aria-label={`Activate ${row.first_name} ${row.last_name}`}
          color="success"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.success}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Clear filters handler
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPage(0);
  };

  return (
    <Box sx={{ py: 0, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.125rem' }}>
            Clinic Staff Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Manage clinic staff accounts and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          Add Staff Member
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={handleClearFilters}
        placeholder="Search by name, email, or phone..."
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      {/* Stats */}
      <Box sx={{ mb: 1, mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {totalCount} total staff member{totalCount !== 1 ? 's' : ''}
          {statusFilter !== 'all' && ` (filtered by ${statusFilter})`}
        </Typography>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={staff}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortDir={sortOrder}
        loading={loading}
        error={error}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortOrder(dir);
        }}
        getRowId={(row) => row.id}
        actionsSlot={actionsSlot}
        emptyMessage="No staff members found. Click 'Add Staff Member' to get started."
        ariaLabel="clinic staff table"
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Staff Member Details</DialogTitle>
        <DialogContent>
          {selectedStaff && (
            <Box sx={{ pt: tokens.space.x2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedStaff.first_name} {selectedStaff.last_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedStaff.status} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedStaff.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedStaff.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Added</Typography>
                  <Typography variant="body1">
                    {selectedStaff.created_at ? new Date(selectedStaff.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Staff Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: tokens.space.x2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Staff Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: tokens.space.x2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Staff member will receive login credentials. Make sure to save the password!
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  helperText="This will be their login username"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Initial Password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  helperText="Auto-generated. Staff can change on first login."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={copyPassword} edge="end" title="Copy password">
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton onClick={regeneratePassword} edge="end" title="Generate new password">
                          <RefreshIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateStaff} 
            variant="contained" 
            disabled={loading || !createForm.first_name || !createForm.last_name || !createForm.email}
          >
            {loading ? 'Creating...' : 'Create Staff Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction === 'activate' ? 'Activate Staff Member' : 'Deactivate Staff Member'}
        message={
          selectedStaff
            ? `Are you sure you want to ${confirmAction} ${selectedStaff.first_name} ${selectedStaff.last_name}?`
            : ''
        }
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setSelectedStaff(null);
          setConfirmAction(null);
        }}
        confirmText={confirmAction === 'activate' ? 'Activate' : 'Deactivate'}
        severity={confirmAction === 'deactivate' ? 'warning' : 'info'}
      />
    </Box>
  );
};

export default ClinicStaffManagement;
